import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { AppLayout } from '../components/AppLayout';
import {
  getStoredAdminHash,
  isAdminAuthenticated,
  loginAdmin,
} from '../services/adminAuth';
import { colors } from '../theme';

export function AdminLoginPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function checkSession() {
      // Se non c’è cookie, niente da verificare
      if (!getStoredAdminHash()) {
        if (!cancelled) setChecking(false);
        return;
      }
      try {
        const ok = await isAdminAuthenticated();
        if (!cancelled && ok) {
          navigate('/admin', { replace: true });
          return;
        }
      } catch {
        // resta sul login
      }
      if (!cancelled) setChecking(false);
    }

    checkSession();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const ok = await loginAdmin(password);
      if (ok) {
        navigate('/admin', { replace: true });
      } else {
        setError('Password non corretta.');
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Impossibile verificare la password. Controlla Firestore.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout
      title="Area admin"
      hideAdmin
      maxWidth="lg"
      leading={
        <IconButton
          color="inherit"
          edge="start"
          aria-label="Torna al calendario"
          onClick={() => navigate('/')}
          size="large"
        >
          <ArrowBackIcon />
        </IconButton>
      }
    >
      <Box
        sx={{
          maxWidth: 420,
          mx: 'auto',
          mt: { xs: 2, sm: 6 },
        }}
      >
        <Card>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            {checking ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={2.5} component="form" onSubmit={handleSubmit}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: colors.primaryContainer,
                      color: colors.primary,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1.5,
                    }}
                  >
                    <LockOutlinedIcon />
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: colors.primary }}
                  >
                    Accesso staff
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Inserisci la password per gestire le prenotazioni.
                  </Typography>
                </Box>

                {error && <Alert severity="error">{error}</Alert>}

                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(null);
                  }}
                  required
                  fullWidth
                  autoFocus
                  autoComplete="current-password"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={
                              showPassword
                                ? 'Nascondi password'
                                : 'Mostra password'
                            }
                            onClick={() => setShowPassword((v) => !v)}
                            onMouseDown={(e) => e.preventDefault()}
                            edge="end"
                          >
                            {showPassword ? (
                              <VisibilityOffOutlinedIcon />
                            ) : (
                              <VisibilityOutlinedIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={submitting}
                >
                  {submitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Entra'
                  )}
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>
    </AppLayout>
  );
}
